import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { siteData, setSiteData, assembleSiteData } from "@/lib/aht/pageData";
import { proposals } from "@/data/proposals";
import { isEditMode } from "@/theme/themes";
import { Nav } from "@/components/aht/Nav";
import { DraftPill, ThemePill } from "@/components/aht/Overlays";
import { Deferred } from "@/components/aht/Deferred";
import { CheckPanel } from "@/components/aht/CheckPanel";
import { StatusPage } from "@/components/aht/StatusPage";
import { useActiveSection, useKeyboardStops } from "@/lib/aht/hooks";
import { checkProceeds, checkSummaries, enrichComps, summarizeGroups } from "@/lib/aht/compute";
import Hero from "@/sections/Hero";

// Everything below the hero ships in one code-split chunk ("below") and mounts as it nears the
// viewport. Page order matters. A section renders only when the proposal's data includes it.
const BELOW = [
  { id: "your-home", tone: "mist", load: () => import(/* webpackChunkName: "below" */ "@/sections/HomeCompared") },
  { id: "nearby", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/NearbySales") },
  { id: "market", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/Market") },
  { id: "pricing", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/fits/WhereFits") },
  { id: "pricing-strategy", tone: "mist", load: () => import(/* webpackChunkName: "below" */ "@/sections/Pricing") },
  { id: "launch", tone: "ink", load: () => import(/* webpackChunkName: "below" */ "@/sections/Launch") },
  { id: "getting-ready", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/GettingReady") },
  { id: "marketing", tone: "mist", load: () => import(/* webpackChunkName: "below" */ "@/sections/Marketing") },
  { id: "communication", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/Communication") },
  { id: "marketing-plan", tone: "mist", load: () => import(/* webpackChunkName: "below" */ "@/sections/MarketingPlan") },
  { id: "offers", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/Offers") },
  { id: "proceeds", tone: "mist", load: () => import(/* webpackChunkName: "below" */ "@/sections/Proceeds") },
  { id: "why-adam", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/WhyAdam") },
  { id: "easy-exit", tone: "ink", load: () => import(/* webpackChunkName: "below" */ "@/sections/EasyExit") },
  { id: "timeline", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/Timeline") },
  { id: "contact", tone: "ink", load: () => import(/* webpackChunkName: "below" */ "@/sections/FinalCta") },
];
const FOOTER = { id: "footer", tone: "paper", load: () => import(/* webpackChunkName: "below" */ "@/sections/Footer") };

// /<code> -> proposal code; ?check=1 -> on-screen validation report.
export function readRoute() {
  const code = decodeURIComponent(window.location.pathname.split("/")[1] || "").trim();
  const check = new URLSearchParams(window.location.search).get("check") === "1";
  return { code, check };
}

const isExpired = (meta, now = new Date()) =>
  meta.status === "expired" || (typeof meta.expiresOn === "string" && !Number.isNaN(Date.parse(meta.expiresOn)) && new Date(`${meta.expiresOn}T23:59:59`) < now);

// Confirms the runtime math against the proposal's checks. Results are exposed on <html>.
function useNumberChecks() {
  useEffect(() => {
    const root = document.documentElement;
    const { comps, compsSummaryCheck, proceeds } = siteData;
    const summary = compsSummaryCheck ? checkSummaries(summarizeGroups(enrichComps(comps)), compsSummaryCheck) : { pass: true, problems: [] };
    const net = proceeds ? checkProceeds(proceeds) : { pass: true, problems: [] };
    root.setAttribute("data-comps-check", summary.pass ? "pass" : "fail");
    root.setAttribute("data-proceeds-check", net.pass ? "pass" : "fail");
    if (!summary.pass) console.warn("Comps summary check failed", summary.problems);
    if (!net.pass) console.warn("Proceeds math check failed", net.problems);
  }, []);
}

function Proposal({ proposal, check }) {
  const { ui, meta, sections } = siteData;
  const ids = useMemo(() => Object.keys(ui.sectionNav), [ui.sectionNav]);
  const active = useActiveSection(ids);
  useKeyboardStops(true);
  useNumberChecks();
  const edit = useMemo(() => isEditMode(), []);
  const below = BELOW.filter((s) => sections.includes(s.id));

  return (
    <div className="min-h-screen bg-paper text-ink" style={{ paddingTop: "var(--nav-height)" }} data-testid="proposal-page" data-code={meta.code}>
      <a
        href="#your-home"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[90] focus:rounded-full focus:bg-ink focus:px-4 focus:py-2 focus:text-paper"
        data-testid="skip-link"
      >
        {ui.skipLink}
      </a>
      <Nav active={ui.sectionNav[active]} />
      {meta.isDraft && <DraftPill />}
      {edit && <ThemePill />}
      {(meta.isDraft || check) && <CheckPanel proposal={proposal} mode={check ? "report" : "draft"} />}
      <main id="main" style={{ overflowX: "clip" }}>
        <Hero />
        {below.map((s, i) => (
          <Deferred key={s.id} id={s.id} order={i} tone={s.tone} load={s.load} />
        ))}
      </main>
      <Deferred id={FOOTER.id} order={below.length} tone={FOOTER.tone} load={FOOTER.load} minHeight="40vh" />
    </div>
  );
}

export default function App() {
  const [state, setState] = useState({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { code, check } = readRoute();
      const loader = code && Object.prototype.hasOwnProperty.call(proposals, code) ? proposals[code] : null;
      const fail = (kind) => {
        setSiteData(assembleSiteData(null));
        document.title = siteData.ui.pageTitle;
        if (!cancelled) setState({ kind });
      };
      if (!loader) return fail("unavailable");
      let proposal;
      try {
        proposal = (await loader()).default;
      } catch (e) {
        console.error("Proposal failed to load", e);
        return fail("unavailable");
      }
      const meta = (proposal && proposal.meta) || {};
      if (meta.status === "withdrawn" || !meta.code) return fail("unavailable");
      if (isExpired(meta)) return fail("expired");
      setSiteData(assembleSiteData(proposal));
      document.title = siteData.ui.pageTitle;
      if (!cancelled) setState({ kind: "ready", proposal, check });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") return <div className="min-h-screen bg-paper" aria-busy="true" data-testid="loading" />;
  if (state.kind === "ready") return <Proposal proposal={state.proposal} check={state.check} />;
  return <StatusPage kind={state.kind} />;
}
